import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Term } from '@/entities/Courses';
import { Svg } from '@/shared/ui/Svg';
import EditIcon from '@/shared/assets/icons/edit.svg';
import TrashIcon from '@/shared/assets/icons/trash.svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';

interface TermsColumnsProps {
  onRowClick?: (term: Term) => void;
  onEdit?: (term: Term) => void;
  onDelete?: (term: Term) => void;
}

const formatTermDate = (value?: string | null) => {
  if (!value) return '--------';

  const date = new Date(value);

  if (Number.isNaN(date.getTime()) || date.getTime() === 0) {
    return '---------';
  }

  return format(date, 'dd.MM.yyyy');
};

export const termsColumns = ({
  onRowClick,
  onEdit,
  onDelete,
}: TermsColumnsProps = {}): ColumnDef<Term>[] => [
  {
    header: 'Terms',
    accessorKey: 'number',
    cell: ({ row }) => (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onRowClick?.(row.original);
        }}
        className="text-black-headline font-medium hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#211D55] text-left rounded-sm"
        data-nav-title="true"
      >
        Term {row.original.number}
      </button>
    ),
  },
  {
    header: 'Students on the course',
    accessorKey: 'studentCount',
    cell: ({ row }) => <span className="text-black-headline">{row.original.studentCount}</span>,
  },
  {
    header: 'Modules',
    accessorKey: 'modulesCount',
    cell: ({ row }) => <span className="text-black-headline">{row.original.modulesCount}</span>,
  },
  {
    header: 'Duration',
    accessorKey: 'totalLessonsDuration',
    cell: ({ row }) => {
      const hours = Math.floor(row.original.totalLessonsDuration / 60);
      const minutes = row.original.totalLessonsDuration % 60;
      return (
        <span className="text-black-headline">
          {hours > 0 ? `${hours} h ` : ''}
          {minutes > 0 ? `${minutes} min` : ''}
        </span>
      );
    },
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Start Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'startDate',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatTermDate(row.original.startDate)}</span>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>End Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'endDate',
    cell: ({ row }) => (
      <span className="text-black-headline">{formatTermDate(row.original.endDate)}</span>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex items-center justify-end gap-1">
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row.original);
            }}
            className="p-2 hover:bg-grey-10 rounded-lg transition-colors text-grey-secondary"
            aria-label="Edit term"
          >
            <Svg Icon={EditIcon} width={20} height={20} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.original);
            }}
            className="p-2 hover:bg-grey-10 rounded-lg transition-colors text-icon-dizlike"
            aria-label="Delete term"
          >
            <Svg Icon={TrashIcon} width={20} height={20} />
          </button>
        )}
      </div>
    ),
  },
];
